import {
  createAction,
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  props as actionProps
} from '@ngrx/store';
import {BuyerIdName} from './buyers-edit/buyers-edit.component';
import {PubmaticShareConfig} from '../../../../segment-multi-share/segment-share.service';

export interface PubmaticBuyer extends BuyerIdName {
  id: number;
  name: string;
}

export interface ChangedPubmaticBuyerDTO {
  id: number;
  type: string;
  deleted: boolean;
}

export interface PubmaticViewModel {
  price: string;
  id: string;
  buyerIds: number[];
  buyers: PubmaticBuyer[];
}

export interface PubmaticState {
  models: { [id: string]: PubmaticViewModel },
  buyers: PubmaticBuyer[]
}

export interface PubmaticEditRequestModel {
  pubmaticExternalId: string;
  config: PubmaticShareConfig;
  segmentId: number;
  accountId: number;
}

export const initialState: Readonly<PubmaticState> = {models: {}, buyers: []};

export const pubmaticFeatureSelector = createFeatureSelector<PubmaticState>('pubmatic');
export const selectPubmaticModelById = (id: string) => createSelector(
  pubmaticFeatureSelector,
  (state) => state.models[id]
);

export const selectPubmaticBuyers = createSelector(
  pubmaticFeatureSelector,
  (state) => state.buyers
);

export const loadPubmaticViewModel = createAction(
  '[Pubmatic] Load model',
  actionProps<{ pubmaticExternalId: string }>()
);

export const loadPubmaticBuyers = createAction(
  '[Pubmatic] Load buyers'
);

export const pubmaticBuyersLoaded = createAction(
  '[Pubmatic] Loaded buyers',
  actionProps<{ buyers: PubmaticBuyer[] }>()
);

export const pubmaticViewModelLoaded = createAction(
  '[Pubmatic] Loaded model',
  actionProps<{ model: PubmaticViewModel }>()
);
export const pubmaticViewModelUpdated = createAction(
  '[Pubmatic] Updated model',
  actionProps<{ model: PubmaticViewModel }>()
);

export const editPubmatic = createAction(
  '[Pubmatic] Edit model]',
  actionProps<{ request: PubmaticEditRequestModel }>()
);

export const pubmaticReducer = createReducer(
  initialState,
  on(pubmaticViewModelLoaded, pubmaticViewModelUpdated, (state, {model}) => {
    const buyersOfModel = state.buyers.filter(buyer => model.buyerIds.includes(buyer.id));
    return {
      ...state,
      models: {
        ...state.models, [model.id]: {...model, buyers: buyersOfModel, buyerIds: model.buyerIds}
      },
    };
  }),
  on(pubmaticBuyersLoaded, (state, {buyers}) => {
    // buyers are loaded on-demand during first time a pubmatic component loads, and the details of pubmatic share is loaded quicker
    // because there's unfortunate pubmatic buyers model, that returns only buyer ids, we have to enrich this data with buyers fetched
    // from cache, hence the recalculation of pubmatic detail models here
    const modelsWithBuyers = Object.values(state.models).map(model => ({
      ...model,
      buyers: buyers.filter(buyer => model.buyerIds.includes(buyer.id))
    }));
    return ({
      ...state,
      buyers: [...buyers],
      models: modelsWithBuyers.reduce((accumulator, current) => ({
        ...accumulator,
        [current.id]: current
      }), {})
    });
  })
);
